export const getRetailReturnPolicy = (posProfile, isAdminRole = false) => {
    const allowsLesserValueExchange =
        Number(posProfile?.allow_lesser_value_exchange) === 1;
    const cashReturnAllowed = allowsLesserValueExchange || Boolean(isAdminRole);

    return {
        allowsLesserValueExchange,
        cashReturnAllowed,
        exchangeRequired: !cashReturnAllowed,
        defaultMode: allowsLesserValueExchange ? "cash" : "exchange",
    };
};

export const isFiveDigitInvoiceSuffix = (value) =>
    /^\d{5}$/.test(String(value || "").trim());

export const getTrailingScannerLetterCandidate = (value) => {
    const normalizedValue = String(value || "").trim();
    if (normalizedValue.length < 2 || !/[A-Za-z]$/.test(normalizedValue)) {
        return null;
    }

    const candidate = normalizedValue.slice(0, -1).trim();
    return candidate || null;
};

const getBarcodeValue = (barcode) =>
    String(
        barcode && typeof barcode === "object" ? barcode.barcode : barcode || ""
    ).trim();

export const getInvoiceItemCodesForBarcode = (invoiceItems, barcode) => {
    const normalizedBarcode = String(barcode || "").trim();
    if (!normalizedBarcode) return [];

    const matchingItemCodes = new Set(
        (Array.isArray(invoiceItems) ? invoiceItems : [])
            .filter((item) =>
                (Array.isArray(item?.barcodes) ? item.barcodes : []).some(
                    (entry) => getBarcodeValue(entry) === normalizedBarcode
                )
            )
            .map((item) => String(item?.item_code || "").trim())
            .filter(Boolean)
    );

    return Array.from(matchingItemCodes);
};

export const getUniqueInvoiceItemCodeForBarcode = (invoiceItems, barcode) => {
    const matchingItemCodes = getInvoiceItemCodesForBarcode(
        invoiceItems,
        barcode
    );
    return matchingItemCodes.length === 1 ? matchingItemCodes[0] : null;
};

export const resolveScannedInvoiceItemCode = async ({
    scanValue,
    invoiceItems,
    resolveBarcodeItemCode,
}) => {
    const normalizedScan = String(scanValue || "").trim();
    if (!normalizedScan) return { itemCode: null, correction: null };

    const exactInvoiceItemCodes = getInvoiceItemCodesForBarcode(
        invoiceItems,
        normalizedScan
    );
    if (exactInvoiceItemCodes.length === 1) {
        return { itemCode: exactInvoiceItemCodes[0], correction: null };
    }

    let exactLookupError = null;
    let exactItemCode = null;
    try {
        exactItemCode = await resolveBarcodeItemCode(normalizedScan);
    } catch (error) {
        exactLookupError = error;
    }
    if (exactItemCode) {
        return { itemCode: exactItemCode, correction: null };
    }

    const correctionCandidate = getTrailingScannerLetterCandidate(
        normalizedScan
    );
    if (!correctionCandidate) {
        if (exactLookupError) throw exactLookupError;
        return { itemCode: null, correction: null };
    }

    const candidateInvoiceItemCodes = getInvoiceItemCodesForBarcode(
        invoiceItems,
        correctionCandidate
    );
    if (candidateInvoiceItemCodes.length === 1) {
        return {
            itemCode: candidateInvoiceItemCodes[0],
            correction: {
                correctedValue: correctionCandidate,
                ignoredSuffix: normalizedScan.slice(-1),
            },
        };
    }
    // A corrected barcode shared by different invoice items is not safe to
    // auto-accept, even if the generic barcode endpoint returns one row.
    if (candidateInvoiceItemCodes.length > 1) {
        return { itemCode: null, correction: null };
    }
    // A failed exact lookup can be a network or server problem. Do not turn it
    // into a different remote lookup unless the invoice data proved the match.
    if (exactLookupError) throw exactLookupError;

    const correctedItemCode = await resolveBarcodeItemCode(
        correctionCandidate
    );
    const correctedItemBelongsToInvoice = (
        Array.isArray(invoiceItems) ? invoiceItems : []
    ).some((item) => item.item_code === correctedItemCode);
    if (!correctedItemCode || !correctedItemBelongsToInvoice) {
        return { itemCode: null, correction: null };
    }

    return {
        itemCode: correctedItemCode,
        correction: {
            correctedValue: correctionCandidate,
            ignoredSuffix: normalizedScan.slice(-1),
        },
    };
};

export const isAddonChildCartEntry = (entry) => {
    const item = entry?.item || entry || {};
    const parentItem = entry?.parent_item ?? item?.parent_item;

    // Add-on rows use a string parent identifier. Retail variants also expose
    // `parent_item`, but as an object containing their template metadata; those
    // are normal sale lines and must remain eligible as exchange replacements.
    if (typeof parentItem !== "string") return false;
    const normalizedParent = parentItem.trim();
    return Boolean(normalizedParent && normalizedParent !== "NULL");
};

export const getReplacementItemKey = (entry) => {
    const item = entry?.item || entry || {};
    const itemCode = String(item?.item_code || entry?.item_code || "").trim();
    const uom = String(
        item?.uom || item?.stock_uom || entry?.uom || ""
    ).trim();
    return itemCode ? `${itemCode}\u0000${uom}` : "";
};

export const normalizeReplacementDiscountPercentage = (value) => {
    const percentage = Number(value);
    return Number.isFinite(percentage) && percentage >= 0 && percentage <= 100
        ? percentage
        : 0;
};

export const buildReplacementItemsFromCart = (cartItems = []) => {
    const replacementsByItem = new Map();

    (Array.isArray(cartItems) ? cartItems : []).forEach((entry) => {
        const item = entry?.item || entry || {};
        if (isAddonChildCartEntry(entry)) return;

        const itemCode = String(
            item?.item_code || entry?.item_code || ""
        ).trim();
        const qty = Number(entry?.qty ?? item?.qty ?? 0);
        const uom = String(
            item?.uom || item?.stock_uom || entry?.uom || ""
        ).trim();
        if (!itemCode || !Number.isFinite(qty) || qty <= 0) return;

        const key = `${itemCode}\u0000${uom}`;
        const current = replacementsByItem.get(key);
        replacementsByItem.set(key, {
            item_code: itemCode,
            qty: Number((Number(current?.qty || 0) + qty).toFixed(9)),
            ...(uom ? { uom } : {}),
        });
    });

    return Array.from(replacementsByItem.values());
};

export const applyReplacementItemDiscounts = (
    replacementItems = [],
    discountPercentagesByItem = {}
) =>
    (Array.isArray(replacementItems) ? replacementItems : []).map((item) => {
        const discountPercentage = normalizeReplacementDiscountPercentage(
            discountPercentagesByItem?.[getReplacementItemKey(item)]
        );
        if (!discountPercentage) return item;
        return {
            ...item,
            discount_percentage: discountPercentage,
        };
    });

export const isReplacementDiscountAcknowledged = ({
    quote,
    replacementItems = [],
    replacementOrderDiscountPercentage = 0,
}) => {
    const requestedOrderDiscount = normalizeReplacementDiscountPercentage(
        replacementOrderDiscountPercentage
    );
    const requestedItemDiscounts = (Array.isArray(replacementItems)
        ? replacementItems
        : []
    )
        .map((item) => ({
            key: getReplacementItemKey(item),
            percentage: normalizeReplacementDiscountPercentage(
                item?.discount_percentage
            ),
        }))
        .filter((item) => item.key && item.percentage > 0);

    if (!requestedOrderDiscount && !requestedItemDiscounts.length) return true;

    const acknowledgment = quote?.replacement_discounts;
    if (!acknowledgment || acknowledgment.validated !== true) return false;
    if (
        normalizeReplacementDiscountPercentage(
            acknowledgment.order_percentage
        ) !== requestedOrderDiscount
    ) {
        return false;
    }

    const appliedItemDiscounts = new Map(
        (Array.isArray(acknowledgment.items) ? acknowledgment.items : []).map(
            (item) => [
                getReplacementItemKey(item),
                normalizeReplacementDiscountPercentage(
                    item?.discount_percentage
                ),
            ]
        )
    );
    return requestedItemDiscounts.every(
        (item) => appliedItemDiscounts.get(item.key) === item.percentage
    );
};

export const buildLocalExchangeQuote = ({
    returnItems = [],
    replacementCartItems = [],
    replacementItemDiscounts = {},
    replacementOrderDiscountPercentage = 0,
    posProfile,
    currency = "",
}) => {
    const returnCredit = (Array.isArray(returnItems) ? returnItems : []).reduce(
        (total, item) => {
            const qty = Number(item?.selected_qty ?? item?.qty ?? 0);
            const rate = Number(item?.rate ?? item?.price_list_rate ?? 0);
            return total + (Number.isFinite(qty * rate) ? qty * rate : 0);
        },
        0
    );
    const replacementTotal = (
        Array.isArray(replacementCartItems) ? replacementCartItems : []
    ).reduce((total, entry) => {
        if (isAddonChildCartEntry(entry)) return total;
        const item = entry?.item || entry || {};
        const qty = Number(entry?.qty ?? item?.qty ?? 0);
        const rate = Number(
            item?.standard_rate ??
                item?.price_list_rate ??
                item?.rate ??
                entry?.rate ??
                0
        );
        const lineDiscountPercentage = normalizeReplacementDiscountPercentage(
            replacementItemDiscounts?.[getReplacementItemKey(entry)]
        );
        const discountedRate = rate * (1 - lineDiscountPercentage / 100);
        return (
            total +
            (Number.isFinite(qty * discountedRate) ? qty * discountedRate : 0)
        );
    }, 0);
    const orderDiscountPercentage = normalizeReplacementDiscountPercentage(
        replacementOrderDiscountPercentage
    );
    const replacementTotalAfterDiscount =
        replacementTotal * (1 - orderDiscountPercentage / 100);
    if (returnCredit <= 0 || replacementTotalAfterDiscount <= 0) return null;

    const allowsLesserValueExchange =
        Number(posProfile?.allow_lesser_value_exchange) === 1;
    const differenceDue = replacementTotalAfterDiscount - returnCredit;
    const paymentModes = (posProfile?.payments || [])
        .map((payment) => payment?.mode_of_payment)
        .filter(Boolean);
    const defaultPaymentMode =
        posProfile?.payments?.find(
            (payment) => Number(payment?.default) === 1
        )?.mode_of_payment || paymentModes[0];

    return {
        preview: true,
        is_estimated: true,
        return_credit: returnCredit,
        replacement_total: replacementTotalAfterDiscount,
        difference_due: differenceDue,
        payment_due: Math.max(differenceDue, 0),
        refund_amount: Math.abs(Math.min(differenceDue, 0)),
        is_value_sufficient:
            replacementTotalAfterDiscount + 0.0001 >= returnCredit ||
            allowsLesserValueExchange,
        allows_lesser_value_exchange: allowsLesserValueExchange,
        currency,
        payment_modes: paymentModes,
        default_payment_mode: defaultPaymentMode,
    };
};

export const buildExchangePaymentDetails = (quote, modeOfPayment) => {
    const paymentDue = Number(quote?.payment_due || 0);
    const normalizedMode = String(modeOfPayment || "").trim();
    if (!Number.isFinite(paymentDue) || paymentDue <= 0 || !normalizedMode) {
        return [];
    }

    return [
        {
            mode_of_payment: normalizedMode,
            amount: paymentDue,
        },
    ];
};
